import XIcon from '@mui/icons-material/X';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/core/hooks/useAuth';
import { getUserData, type UserData } from '@/core/services/claim';
import { getTwitterAuthLink, tweetWithMedia } from '@/core/services/twitter';
import type { Claim } from '@/core/types';

const TWITTER_RETURN_URL_KEY = 'twitter_return_url';
const TWITTER_TOKEN_SECRET_KEY = 'twitter_oauth_token_secret';

// Twitter文字数上限（重み付き）
const MAX_TWEET_WEIGHT = 280;
// URLは常に23文字扱い（t.co短縮）
const URL_WEIGHT = 23;

// raceAlias → ハッシュタグのマッピング（暫定）
const HASHTAG_MAP: Record<string, string> = {
  ehime2026: '愛媛マラソン',
};

/**
 * Twitter公式の重み付き文字数を計算
 * @see https://github.com/twitter/twitter-text/blob/master/config/v3.json
 *
 * Weight 1: U+0000-U+10FF, U+2000-U+200D, U+2010-U+201F, U+2020-U+202F
 * Weight 2: それ以外（CJK、絵文字など）
 */
function getWeightedLength(text: string): number {
  let weight = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    const isWeight1 =
      (code >= 0x0000 && code <= 0x10ff) ||
      (code >= 0x2000 && code <= 0x200d) ||
      (code >= 0x2010 && code <= 0x201f) ||
      (code >= 0x2020 && code <= 0x202f);
    weight += isWeight1 ? 1 : 2;
  }
  return weight;
}

/** ハッシュタグを取得 */
function getHashtags(raceAlias: string): string {
  const raceTag = HASHTAG_MAP[raceAlias] ?? raceAlias;
  return `#${raceTag} #RunPort`;
}

/** ギャラリーURLを取得 */
function getGalleryUrl(raceAlias: string): string {
  return `https://nft.runport.run/${raceAlias}/certs`;
}

interface TwitterShareButtonProps {
  claim: Claim;
  iconOnly?: boolean;
}

export function TwitterShareButton({ claim, iconOnly = false }: TwitterShareButtonProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [userData, setUserData] = useState<UserData | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tweetText, setTweetText] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [postedTweet, setPostedTweet] = useState<{ screenName: string; tweetId: string } | null>(null);

  const hasTwitterCredential = !!userData?.twitterCredential;

  // 固定部分（URL + ハッシュタグ）
  const suffix = useMemo(() => {
    const url = getGalleryUrl(claim.raceAlias);
    const hashtags = getHashtags(claim.raceAlias);
    return `\n\n${url}\n\n${hashtags}`;
  }, [claim.raceAlias]);

  // 固定部分の重み（URLは23固定 + 改行 + ハッシュタグ）
  const suffixWeight = useMemo(() => {
    const hashtags = getHashtags(claim.raceAlias);
    // URL=23 + 改行4つ=4 + ハッシュタグの重み
    return URL_WEIGHT + 4 + getWeightedLength(hashtags);
  }, [claim.raceAlias]);

  // ユーザー編集部分の上限（負にならないようクランプ）
  const maxEditableWeight = Math.max(0, MAX_TWEET_WEIGHT - suffixWeight);

  // 現在の編集部分の重み
  const currentWeight = getWeightedLength(tweetText);
  const remainingWeight = maxEditableWeight - currentWeight;

  // ユーザーデータを取得
  const fetchUserData = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getUserData(user.uid);
      setUserData(data);
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setError('ユーザー情報の取得に失敗しました。再読み込みしてください。');
    } finally {
      setInitialLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // 初期ツイートテキストを設定
  useEffect(() => {
    const defaultText = `${claim.raceName}完走！🔥\nグロス ${claim.time} / ネット ${claim.netTime}`;
    setTweetText(defaultText);
  }, [claim]);

  // twitter=success クエリパラメータがあればダイアログを開く
  useEffect(() => {
    if (searchParams.get('twitter') === 'success') {
      // クエリパラメータを削除
      searchParams.delete('twitter');
      setSearchParams(searchParams, { replace: true });
      // ユーザーデータを再取得してダイアログを開く
      fetchUserData().then(() => {
        setDialogOpen(true);
      });
    }
  }, [
    searchParams,
    setSearchParams, // ユーザーデータを再取得してダイアログを開く
    fetchUserData,
  ]);

  // X連携を開始
  const handleConnectTwitter = async () => {
    setLoading(true);
    setError(null);

    try {
      const callbackUrl = `${window.location.origin}/auth/twitter/callback`;
      const { authUrl, oauthTokenSecret } = await getTwitterAuthLink(callbackUrl);

      // 現在のURLと oauth_token_secret を保存
      sessionStorage.setItem(TWITTER_RETURN_URL_KEY, location.pathname);
      sessionStorage.setItem(TWITTER_TOKEN_SECRET_KEY, oauthTokenSecret);

      // X認証ページへリダイレクト
      window.location.href = authUrl;
    } catch (err) {
      console.error('Twitter connect error:', err);
      setError('X連携の開始に失敗しました');
      setLoading(false);
    }
  };

  // ツイートを投稿
  const handlePostTweet = async () => {
    setLoading(true);
    setError(null);

    try {
      // ユーザー編集部分 + 固定部分を結合
      const fullTweet = tweetText + suffix;
      const result = await tweetWithMedia({
        claimId: claim.id,
        tweet: fullTweet,
      });
      setPostedTweet(result);
    } catch (err) {
      console.error('Tweet error:', err);
      setError('投稿に失敗しました。しばらく経ってからお試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (hasTwitterCredential) {
      setDialogOpen(true);
    } else {
      handleConnectTwitter();
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError(null);
    setPostedTweet(null);
  };

  const isValidTweet = tweetText.length > 0 && remainingWeight >= 0;

  if (initialLoading) {
    return null;
  }

  return (
    <>
      {iconOnly ? (
        <IconButton
          onClick={handleOpenDialog}
          disabled={loading}
          aria-label="Xでシェア"
          sx={{
            bgcolor: '#000',
            color: '#fff',
            width: 48,
            height: 48,
            '&:hover': { bgcolor: '#333' },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : <XIcon />}
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <XIcon />}
          onClick={handleOpenDialog}
          disabled={loading}
          sx={{
            bgcolor: '#000',
            height: 48,
            fontWeight: 600,
            '&:hover': { bgcolor: '#333' },
          }}
          fullWidth
        >
          {hasTwitterCredential ? 'Xでシェア' : 'Xと連携してシェア'}
        </Button>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Xでシェア</DialogTitle>
        <DialogContent>
          {postedTweet ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                投稿しました！
              </Typography>
              <Link
                href={`https://twitter.com/${postedTweet.screenName}/status/${postedTweet.tweetId}`}
                target="_blank"
                rel="noopener"
              >
                ポストを見る
              </Link>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              <TextField
                multiline
                rows={3}
                fullWidth
                value={tweetText}
                onChange={(e) => setTweetText(e.target.value)}
                placeholder="ツイートを入力..."
                disabled={loading}
                error={remainingWeight < 0}
                helperText={`残り ${remainingWeight} 文字（日本語は2文字換算）`}
                sx={{ mt: 1 }}
              />
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  whiteSpace: 'pre-wrap',
                  color: 'text.secondary',
                }}
              >
                {suffix.trim()}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                ※ 上記URL・ハッシュタグと動画が自動的に添付されます
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{postedTweet ? '閉じる' : 'キャンセル'}</Button>
          {!postedTweet && (
            <Button
              variant="contained"
              onClick={handlePostTweet}
              disabled={loading || !isValidTweet}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <XIcon />}
              sx={{
                bgcolor: '#000',
                '&:hover': { bgcolor: '#333' },
              }}
            >
              投稿する
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
